import { useErrorBoundary } from 'react-error-boundary';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import Message from '@deliveryhero/pd-cookbook/components/Message';
import Typography from '@deliveryhero/pd-cookbook/components/Typography';

import { Card } from 'components/Card';
import { LabelText } from 'components/GiftCard/constants';
import { GiftCardRecipients } from 'components/GiftCard/GiftCardRecipients';
import { OrderSummary } from 'components/GiftCard/OrderSummary';
import { GiftCardAction } from 'components/GiftCard/types';
import { LoadingAnimation } from 'components/LoadingAnimation';
import { useConfiguration } from 'contexts/country-config';
import { format, isAfter } from 'date-fns';
import { GiftCardState } from 'services/api';
import { AppRoute } from 'share/types/routes';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import { FeatureFlag } from 'src/utils/constants';

import { GiftCardSummaryPageProps } from './types';

import './styles.scss';

export const GiftCardSummaryPage = ({
  summaryQuery,
  handleSendGiftCardDetails,
  handleDownloadRecipientCsv,
  onActionClick,
  uploadSuccessStatus
}: GiftCardSummaryPageProps) => {
  const config = useConfiguration();
  const featureFlags = useFeatureFlags();
  const { currencyCode } = config;
  const { showBoundary } = useErrorBoundary();
  const labels = LabelText.GIFT_CARD_SUMMARY_PAGE;
  let statusMessage = <></>;

  if (summaryQuery.isLoading) {
    return <LoadingAnimation />;
  } else if (summaryQuery.isError || !summaryQuery.data) {
    showBoundary(summaryQuery.error);
    return <></>;
  }
  const {
    num_of_recipients,
    recipients,
    is_csv_recipients,
    is_distribute_myself,
    state,
    quantity,
    value,
    theme_image_url,
    message,
    created_at,
    issued_on,
    expires_on,
    external_order_number,
    expiry,
    number_of_failed_recipients,
    subtotal,
    total,
    discount_amount,
    discount_percentage
  } = summaryQuery.data.data;

  const renderDefaultStatusMessage = () => {
    return (
      <Message
        kind="information"
        title={labels.STATUS_COMPLETE_TITLE}
        message={labels.STATUS_COMPLETE_MESSAGE.replace(
          '%currency%',
          currencyCode
        ).replace('%value%', value.toString())}
      />
    );
  };

  const renderPartiallyFailedStatusMessage = () => {
    return (
      <>
        {renderDefaultStatusMessage()}
        {uploadSuccessStatus.success ? (
          <Message
            kind="success"
            title={labels.UPLOAD_SUCCESS_TITLE.replace(
              '%quantity%',
              number_of_failed_recipients.toString()
            )}
            message={labels.UPLOAD_SUCCESS_MESSAGE}
          />
        ) : (
          <Message
            kind="error"
            title={labels.STATUS_PARTIALLY_FAILED_TITLE.replace(
              '%quantity%',
              number_of_failed_recipients.toString()
            )}
            message={labels.STATUS_PARTIALLY_FAILED_MESSAGE.replace(
              '%quantity%',
              number_of_failed_recipients.toString()
            )}
            actionText={labels.STATUS_PARTIALLY_FAILED_CTA}
            onActionClick={() => onActionClick(GiftCardAction.CsvUploadFailed)}
          />
        )}
        {uploadSuccessStatus.error && (
          <Message
            kind="information"
            title={labels.UPLOAD_FAILED_TITLE}
            message={labels.UPLOAD_FAILED_MESSAGE}
          />
        )}
      </>
    );
  };

  switch (state) {
    case GiftCardState.COMPLETE:
      statusMessage =
        featureFlags(FeatureFlag.ResendFailedEmail) &&
        number_of_failed_recipients > 0
          ? renderPartiallyFailedStatusMessage()
          : renderDefaultStatusMessage();
      break;
    // PENDING message shown for PROCESSING state until QC fixes statuses
    case GiftCardState.PROCESSING:
      statusMessage = featureFlags(FeatureFlag.PaymentFlow) ? (
        <Message
          kind="warning"
          title={labels.STATUS_PENDING_TITLE}
          message={labels.STATUS_PENDING_MESSAGE}
          actionText={LabelText.GIFT_CARD_PAYMENT_FLOW.PAYMENT_LINK_TITLE}
          onActionClick={() => onActionClick(GiftCardAction.PaymentFlow)}
        />
      ) : (
        <div className="message-cta-container">
          <Message
            kind="warning"
            title={labels.STATUS_PENDING_TITLE}
            message={labels.STATUS_PENDING_MESSAGE}
          />
        </div>
      );
      break;
    case GiftCardState.PENDING:
      // todo: Inaccessible branch until QC enables PENDING state
      statusMessage = featureFlags(FeatureFlag.PaymentFlow) ? (
        <Message
          kind="warning"
          title={labels.STATUS_PENDING_TITLE}
          message={labels.STATUS_PENDING_MESSAGE}
          actionText={LabelText.GIFT_CARD_PAYMENT_FLOW.PAYMENT_LINK_TITLE}
          onActionClick={() => onActionClick(GiftCardAction.PaymentFlow)}
        />
      ) : (
        <div className="message-cta-container">
          <Message
            kind="warning"
            title={labels.STATUS_PENDING_TITLE}
            message={labels.STATUS_PENDING_MESSAGE}
          />
        </div>
      );
      break;
    case GiftCardState.CANCELLED:
      statusMessage = (
        <div className="message-cta-container">
          <Message
            kind="error"
            title={labels.STATUS_CANCELLED_TITLE}
            message={labels.STATUS_CANCELLED_MESSAGE}
          />
          <a href={`${AppRoute.GiftCard}?action=add`}>
            <Button variant="primary" layout="full-width-primary">
              {labels.REORDER_CTA}
            </Button>
          </a>
        </div>
      );
      break;
    case GiftCardState.PAYMENT_PROCESSING:
      statusMessage = (
        <Message
          kind="information"
          title={labels.STATUS_PAYMENT_PROCESSING_TITLE}
          message={labels.STATUS_PAYMENT_PROCESSING_MESSAGE.replace(
            '%currency%',
            currencyCode
          )
            .replace('%quantity%', quantity.toString())
            .replace('%value%', value.toString())}
        />
      );
      break;
    case GiftCardState.PAYMENT_REJECTED:
      statusMessage = (
        <Message
          kind="error"
          title={labels.STATUS_PAYMENT_PAYMENT_REJECTED_TITLE}
          message={labels.STATUS_PAYMENT_PAYMENT_REJECTED_MESSAGE}
        />
      );
      break;
  }

  return (
    <div className="summary-page-container">
      <div className="summary-page-container__column">
        <Card className="gift-card-theme">
          <div className="gift-card-theme__image">
            <img src={theme_image_url} />
          </div>
          <Typography type="title-sm" as="div">
            {message}
          </Typography>
        </Card>
        <Card>
          <OrderSummary
            config={config}
            orderSummaryDetails={{
              quantity: quantity,
              giftCardValue: value,
              giftCardMessage: message,
              discountAmount: discount_amount,
              discountPercentage: discount_percentage,
              total,
              subtotal
            }}
          />
        </Card>
      </div>
      <div className="summary-page-container__column summary-page-container__column__right">
        {statusMessage}
        <Card>
          <Typography type="title-md" as="div">
            {labels.ORDER_DETAILS_TITLE}
          </Typography>
          <div className="order-detail-container">
            <div className="order-detail-container__cell">
              <Typography type="label-sm-secondary" as="div">
                {labels.ORDER_DETAILS_ORDER_DATE}
              </Typography>
              <Typography type="label-lg" as="div">
                {format(new Date(created_at), 'dd/MM/yyyy')}
              </Typography>
            </div>
            <div className="order-detail-container__cell">
              <Typography type="label-sm-secondary" as="div">
                {labels.ORDER_DETAILS_ORDER_ID}
              </Typography>
              <Typography type="label-lg" as="div">
                {external_order_number}
              </Typography>
            </div>
            {state === GiftCardState.COMPLETE && (
              <div className="order-detail-container__dual-cell">
                <div className="order-detail-date">
                  <div className="order-detail-container_cell">
                    <Typography type="label-sm-secondary" as="div">
                      {labels.ORDER_DETAILS_ISSUED_ON}
                    </Typography>
                    <Typography type="label-lg" as="div">
                      {format(new Date(issued_on), 'dd/MM/yyyy')}
                    </Typography>
                  </div>
                  <div className="order-detail-container_cell">
                    <Typography type="label-sm-secondary" as="div">
                      {isAfter(new Date(), new Date(expires_on))
                        ? labels.ORDER_DETAILS_EXPIRED_ON
                        : labels.ORDER_DETAILS_EXPIRES_ON}
                    </Typography>
                    <Typography type="label-lg" as="div">
                      {format(new Date(expires_on), 'dd/MM/yyyy')}
                    </Typography>
                  </div>
                </div>
                <Message
                  kind="information"
                  message={labels.VALIDITY_MESSAGE.replace(
                    '%duration_period%',
                    expiry
                  )}
                />
              </div>
            )}
          </div>
        </Card>
        <GiftCardRecipients
          num_of_recipients={num_of_recipients}
          recipients={recipients}
          is_csv_recipients={is_csv_recipients}
          is_distribute_myself={is_distribute_myself}
          handleDownloadRecipientCsv={handleDownloadRecipientCsv}
        />
        {state === GiftCardState.COMPLETE && is_distribute_myself && (
          <Button
            variant="primary"
            layout="full-width-primary"
            onClick={() => handleSendGiftCardDetails()}
          >
            {labels.SEND_GIFT_CARD_TO_EMAIL_CTA}
          </Button>
        )}
      </div>
    </div>
  );
};
